"use client";

import { useEffect } from "react";
import { trackGaEvent } from "@/lib/gtag";
import { capture } from "@/lib/analytics";
import { readMarketingCampaign } from "@/lib/signupAttribution";

/**
 * Landing-page funnel instrumentation, mounted once on the marketing home.
 * Keeps page.tsx a server component: CTAs are tagged with `data-analytics`
 * (event name) + optional `data-analytics-loc` (where on the page), and this
 * component listens via delegation. Also reports scroll depth and which
 * sections a visitor actually reaches — together these show where people drop
 * off before converting. Inert without GA (trackGaEvent no-ops).
 */
export function LandingAnalytics() {
  useEffect(() => {
    const campaign = readMarketingCampaign();
    if (campaign) {
      for (const anchor of document.querySelectorAll<HTMLAnchorElement>("a[data-analytics]")) {
        const url = new URL(anchor.href, window.location.origin);
        if (url.origin === window.location.origin && /\/(?:sign-up)$/.test(url.pathname)) {
          url.searchParams.set("src", campaign);
          anchor.href = url.toString();
        }
      }
    }
    // PostHog: landing page reached (top of the activation funnel).
    capture("landing_viewed");

    // 1. CTA / nav click tracking via event delegation.
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      const el = target?.closest?.("[data-analytics]") as HTMLElement | null;
      if (!el) return;
      const event = el.getAttribute("data-analytics");
      if (!event) return;
      if (event === "audience_writer_selected" || event === "audience_editor_selected") {
        capture("audience_selected", { audience: event === "audience_writer_selected" ? "writer" : "editor", location: el.getAttribute("data-analytics-loc") || undefined });
      }
      trackGaEvent(event, {
        location: el.getAttribute("data-analytics-loc") || undefined,
      });
    }
    document.addEventListener("click", onClick, { capture: true });

    // 2. Scroll-depth milestones (fired once each).
    const milestones = [25, 50, 75, 100];
    const fired = new Set<number>();
    function onScroll() {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = Math.round((doc.scrollTop / max) * 100);
      for (const m of milestones) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          trackGaEvent("scroll_depth", { percent: m });
        }
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    // 3. Section views — which parts of the page a visitor actually sees.
    let observer: IntersectionObserver | null = null;
    const sections = Array.from(
      document.querySelectorAll<HTMLElement>("[data-section]"),
    );
    if (sections.length && "IntersectionObserver" in window) {
      const seen = new Set<string>();
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const id = (entry.target as HTMLElement).dataset.section;
            if (entry.isIntersecting && id && !seen.has(id)) {
              seen.add(id);
              trackGaEvent("section_view", { section: id });
            }
          }
        },
        { threshold: 0.4 },
      );
      sections.forEach((s) => observer!.observe(s));
    }

    return () => {
      document.removeEventListener("click", onClick, { capture: true });
      window.removeEventListener("scroll", onScroll);
      observer?.disconnect();
    };
  }, []);

  return null;
}
